import React, { useEffect, useMemo } from 'react';
import FormHandler from '../FormHandler';
import { StyledTextField } from '../styledComponents';
import PasswordField from '../PasswordField';
import Typography from '@mui/material/Typography';
import { usePassword, useUsername } from '../../slices/hooks/useUsersSlice';
import isEmpty from 'lodash/isEmpty';
import useFocus from '../hooks/useFocus';

interface Props {
    isHidden: boolean;
    onAuthenticationRequirementsCheck: (username: string) => void;
    onPasswordLogin: (u: string, p: string) => void;
    isLoading: boolean;
    isPasswordLoginAllowed: boolean;
    hasFetchedRequirements: boolean;
}
const UsernamePasswordForm = ({
    isHidden,
    onAuthenticationRequirementsCheck,
    onPasswordLogin,
    isLoading,
    isPasswordLoginAllowed,
    hasFetchedRequirements,
}: Props) => {
    const { username, updateUsername } = useUsername();
    const { password, updatePassword } = usePassword();
    const usernameInputRef = useFocus();
    const passwordInputRef = useFocus();

    useEffect(() => {
        if (usernameInputRef.current && !isPasswordLoginAllowed) {
            usernameInputRef.current.focus();
        }
    }, [usernameInputRef.current, isPasswordLoginAllowed]);

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isPasswordLoginAllowed && !isEmpty(username)) {
            onPasswordLogin(username, password, false);
        } else {
            // This will trigger the SSO and MFA check and redirect if SSO is enabled.
            onAuthenticationRequirementsCheck(username);
        }
    };

    const isFormSubmitDisabled = useMemo(() => {
        return isLoading || isEmpty(username) || (hasFetchedRequirements && isEmpty(password));
    }, [hasFetchedRequirements, isPasswordLoginAllowed, isLoading, username, password]);

    return (
        <div style={isHidden ? { display: 'none' } : {}}>
            <Typography variant="h5" mb="25px" gutterBottom style={{ textAlign: 'center' }}>
                Please sign in
            </Typography>
            <FormHandler
                onSubmit={handleFormSubmit}
                buttonText={isPasswordLoginAllowed ? 'Sign in' : 'Next'}
                isDisabled={isFormSubmitDisabled}
                isLoading={isLoading}
            >
                <StyledTextField
                    hidden={isPasswordLoginAllowed}
                    fullWidth
                    label="Email or username"
                    variant="standard"
                    margin="normal"
                    value={username}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateUsername(e.target.value)
                    }
                    inputRef={usernameInputRef}
                />
                <PasswordField
                    key={isPasswordLoginAllowed ? 'password-visible' : 'password-hidden'}
                    hidden={!isPasswordLoginAllowed}
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updatePassword(e.target.value)
                    }
                    inputRef={passwordInputRef}
                />
            </FormHandler>
        </div>
    );
};
export default UsernamePasswordForm;
